import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import SearchOptions from '../search/SearchOptions';

// the dropdown's own element stays mounted, while reactstrap swaps the menu element each time it opens or closes
const getCheckboxes = container =>
  container ? [...container.querySelectorAll('.dropdown-menu input[type="checkbox"]')] : [];

/*
A filter button that opens a list of checkboxes. reactstrap's Dropdown only knows menus of
role="menuitem": on these lists its arrow-key handling threw and Escape did nothing. The button
and the list handle their own keys and stop them there: Enter, Space and the arrow keys open the
list on a checkbox, the arrow keys move through it, and Escape closes it and returns focus to the
button. The list is a named group, not a menu.
*/
class InlineFilter extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = { dropdownOpen: false };

    this.handleChange = this.handleChange.bind(this);
    this.handleMenuKeyDown = this.handleMenuKeyDown.bind(this);
    this.handleToggleKeyDown = this.handleToggleKeyDown.bind(this);
    this.onToggle = this.onToggle.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleChange(id, selected) {
    const { keyString: key, onChange } = this.props;

    const updateId = id + (key === 'cc' ? 1 : 0);
    onChange(key, updateId, selected);
  }

  // Enter and Space reach the button's own click (event.detail is 0 for a click made with a key)
  handleToggleKeyDown(event) {
    const { dropdownOpen } = this.state;
    const container = event.currentTarget.parentNode;

    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const isUp = event.key === 'ArrowUp'; // React 16 reuses the event object once the handler returns
      event.preventDefault();
      event.stopPropagation();
      this.setOpen(true, () => {
        const checkboxes = getCheckboxes(container);
        const checkbox = isUp ? checkboxes[checkboxes.length - 1] : checkboxes[0];
        checkbox && checkbox.focus();
      });
    } else if (event.key === 'Escape' && dropdownOpen) {
      event.stopPropagation();
      this.setOpen(false);
    }
  }

  handleMenuKeyDown(event) {
    const container = event.currentTarget.parentNode;
    const checkboxes = getCheckboxes(container);
    const index = checkboxes.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.stopPropagation();
      this.setOpen(false, () => {
        const toggle = container.querySelector('.dropdown-toggle');
        toggle && toggle.focus();
      });
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && checkboxes.length) {
      event.preventDefault();
      event.stopPropagation();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      checkboxes[(index + step + checkboxes.length) % checkboxes.length].focus();
    }
  }

  // called by the button's click and by reactstrap's document listener (a click or Tab outside closes the list)
  onToggle(event) {
    const isOpening = !this.state.dropdownOpen;
    const isKeyboardClick = Boolean(event && event.type === 'click' && event.detail === 0 && isOpening);
    const container = isKeyboardClick ? event.currentTarget.parentNode : null;

    this.setOpen(isOpening, () => {
      const [first] = getCheckboxes(container);
      first && first.focus();
    });
  }

  setOpen(dropdownOpen, callback) {
    this._isMounted && this.setState({ dropdownOpen }, callback);
  }

  render() {
    const { label, options, theme } = this.props;
    const { dropdownOpen } = this.state;
    return (
      <Dropdown isOpen={dropdownOpen} toggle={this.onToggle} className="p-2">
        <DropdownToggle
          caret
          color={theme ? theme : 'falcon-primary'}
          aria-haspopup={false}
          onKeyDown={this.handleToggleKeyDown}
        >
          {label}
        </DropdownToggle>
        <DropdownMenu
          role="group"
          aria-label={label}
          className="p-2 max-vh-25 max-vw-50 overflow-scroll testlet-filter-options"
          onKeyDown={this.handleMenuKeyDown}
        >
          <SearchOptions searchOptions={options} onChange={this.handleChange} />
        </DropdownMenu>
      </Dropdown>
    );
  }
}

InlineFilter.propTypes = {
  keyString: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.string
};

export default InlineFilter;
